import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { usePlaidLink } from 'react-plaid-link';
import { useSearchParams } from 'react-router-dom';
import { Checkbox, Spin } from 'antd';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import {
  createPlaidToken,
  createIdentityVerificationLinkToken,
  addPlaidIdentityVerification,
  addIdentityMatch,
  exchangePublicToken,
  getGUIDValidation,
  getOrganizationSettings,
  getOrganizationSettingsSection,
  getOrganizationSettingsName,
} from 'services/api';
import BrokersafeLogo from 'assets/icons/brokersafe_logo.svg';
import Button from 'components/Button';
import {
  addPlaidLinkCallbackOnSuccess,
  addPlaidLinkCallbackOnEvent,
  addPlaidLinkCallbackOnExit,
} from 'modules/helpers';
import AddWireTransferDetailModal from './AddWireTransferDetailModal';
import { Root } from './styles';
import Verification from './Verification';
import PaymentPreference from './PaymentPreference';
import { Customer, OrganizationSetting } from './types';

const Plaid = () => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [identityVerificationLinkToken, setIdentityVerificationLinkToken] =
    useState<string | null>(null);
  const [checked, setChecked] = useState<CheckboxValueType>(false);
  const [customer, setCustomer] = useState<Customer>();
  const [idVerificationComplete, setIDVerificationComplete] = useState(false);
  const [isIDVSubmitting, setIDVSubmitting] = useState(false);
  const [loadingGUIDValidate, setLoadingGUIDValidated] = useState(true);
  const [searchParams] = useSearchParams();
  const [isVerified, setVerified] = useState(false);
  const [isPaymentPreferenceComplete, setPaymentPreferenceComplete] =
    useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isComplete, setComplete] = useState(false);
  const [isDigital, setIsDigital] = useState(false);
  const [settingSection, setSettingSection] = useState('');
  const settingSectionRef = useRef<string>('');

  const [termsPageSettings, setTermsPageSettings] = useState<
    OrganizationSetting[]
  >([]);
  const [completePageSettings, setCompletePageSettings] = useState<
    OrganizationSetting[]
  >([]);
  const idvPk = useRef<number | undefined>();
  const [iDVResult, setIDVResult] = useState({} as OrganizationSetting);
  const [iDVStatus, setIDVStatus] = useState(false);
  const [idvCompleteSettings, setIDVCompleteSettings] = useState<
    OrganizationSetting[]
  >([]);
  const [idvSuccessCompleteSettings, setIDVSuccessCompleteSettings] = useState<
    OrganizationSetting[]
  >([]);

  const addWireTransferDetailModal = useModal(AddWireTransferDetailModal);

  const validateGUID = useCallback(async () => {
    const guid = searchParams.get('guid');
    if (!guid) {
      setLoadingGUIDValidated(false);
      return;
    }

    try {
      const guidValidationRes = await getGUIDValidation(guid);
      if (guidValidationRes.GUID) {
        setCustomer(guidValidationRes);

        if (guidValidationRes.Plaid_Identity_Verification_Template_ID) {
          const idvLinkTokenRes = await createIdentityVerificationLinkToken(
            guidValidationRes,
          );
          // @ts-ignore
          setIdentityVerificationLinkToken(idvLinkTokenRes.linkToken);
        }

        const linkTokenRes = await createPlaidToken({
          ...guidValidationRes,
          auth: {
            // automated_microdeposits_enabled: true,
            same_day_microdeposits_enabled: true,
            auth_type_select_enabled: true,
            sms_microdeposits_verification_enabled: false,
          },
        });
        // @ts-ignore
        setLinkToken(linkTokenRes.linkToken);

        const termsPageSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact Bank Connect',
          'Terms & Privacy',
        );
        setTermsPageSettings(termsPageSettingsRes);

        const idvCompleteSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact IDV',
          'Completed',
        );
        setIDVCompleteSettings(idvCompleteSettingsRes);

        const idvSuccessCompleteSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact IDV',
          'Completed - IDV Only',
        );
        setIDVSuccessCompleteSettings(idvSuccessCompleteSettingsRes);

        const completeSettingsSectionRes = await getOrganizationSettingsSection(
          guidValidationRes.Customer_PK,
        );
        setSettingSection(completeSettingsSectionRes.Settings_Section);
        settingSectionRef.current = completeSettingsSectionRes.Settings_Section;

        const completePageSettingsRes = await getOrganizationSettings(
          guidValidationRes.Organization_PK,
          'Contact Bank Connect',
          completeSettingsSectionRes.Settings_Section,
        );
        setCompletePageSettings(completePageSettingsRes);
      }
      setLoadingGUIDValidated(false);
    } catch (error) {
      setLoadingGUIDValidated(false);
    }
  }, [searchParams]);

  useEffect(() => {
    validateGUID();
  }, [validateGUID]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      const escrowAccountFlag = searchParams.get('eaf');

      try {
        const tokenRes = await exchangePublicToken({
          public_token,
          metadata,
          customer,
          escrowAccountFlag,
        });
        if (idvPk.current) {
          addIdentityMatch(tokenRes.plaidAccessToken, idvPk.current);
        }

        const verificationStatus = metadata.accounts[0].verification_status;
        const isInstantDeposit = !verificationStatus;
        const completePageSettingsRes = await getOrganizationSettings(
          customer!.Organization_PK,
          'Contact Bank Connect',
          isInstantDeposit
            ? settingSectionRef.current
            : 'Completed - Micro-Deposit',
        );
        setCompletePageSettings(completePageSettingsRes);
        setComplete(true);
        setSubmitting(false);
        addPlaidLinkCallbackOnSuccess(customer, metadata);
      } catch (error) {
        setSubmitting(false);
      }
    },
    onExit: (error, metadata: any) => {
      setSubmitting(false);
      addPlaidLinkCallbackOnExit(customer, error, metadata);
    },
    onEvent: (eventName, metadata: any) => {
      if (eventName === 'OPEN') {
        setSubmitting(true);
      }
      addPlaidLinkCallbackOnEvent(customer, metadata);
    },
  });

  console.log(open);

  console.log(ready);

  const { open: openIDV, ready: readyIDV } = usePlaidLink({
    token: identityVerificationLinkToken,
    onSuccess: async (_, metadata) => {
      try {
        const resIdvData = await addPlaidIdentityVerification({
          customer,
          identity_verification_id: metadata.link_session_id,
        });
        const resOrgSettingName = await getOrganizationSettingsName(
          customer?.Organization_PK!,
          resIdvData.status === 'success'
            ? 'IDV + Wire Transfer Completed - Success'
            : 'IDV + Wire Transfer Completed - Fail',
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        resIdvData.status === 'success'
          ? setIDVStatus(true)
          : setIDVStatus(false);
        setIDVResult(resOrgSettingName);
        idvPk.current = resIdvData.Plaid_Identity_Verification_PK;
        setIDVerificationComplete(true);
        setIDVSubmitting(false);
        addPlaidLinkCallbackOnSuccess(customer, metadata);
      } catch (error) {
        setIDVSubmitting(false);
      }
    },
    onExit: (error, metadata: any) => {
      setIDVSubmitting(false);
      addPlaidLinkCallbackOnExit(customer, error, metadata);
    },
    onEvent: (eventName, metadata: any) => {
      addPlaidLinkCallbackOnEvent(customer, metadata);
    },
  });

  const termsPageHeaderText = useMemo(() => {
    let text = '';
    const headerSetting = termsPageSettings.find(
      (el) => el.Settings_Name === 'Bank Connect - Terms Acceptance',
    );
    if (headerSetting) {
      text = headerSetting.Settings_String || '';
    }
    return text;
  }, [termsPageSettings]);

  const completePageHeaderText = useMemo(() => {
    let text = '';
    let headerSetting;
    if (settingSection === 'Completed - Manual') {
      [headerSetting] = completePageSettings;
    } else {
      headerSetting = completePageSettings.find(
        (el) =>
          el.Settings_Name ===
          (isDigital
            ? 'Bank Connect - Completed'
            : 'Bank Connect - Completed (Check)'),
      );
    }

    if (headerSetting) {
      text = headerSetting.Settings_String || '';
    }
    return text;
  }, [completePageSettings, isDigital, settingSection]);

  const basicUrl = useMemo(() => {
    const env = process.env.REACT_APP_ENV;
    if (env === 'development') {
      return 'https://contact-dev.tifle.io/';
    }

    if (env === 'staging') {
      return 'https://contact-staging.tifle.io/';
    }

    return 'https://contact.tifle.io/';
  }, []);

  const openWireDetail = () => {
    addWireTransferDetailModal.show({
      customerPk: customer?.Customer_PK,
      customerId: customer?.Customer_ID,
      onClose: async () => {
        const resOrgSettingName = await getOrganizationSettingsName(
          customer?.Organization_PK!,
          'IDV + Wire Transfer Completed',
        );
        setIDVResult(resOrgSettingName);
      },
    });
  };

  const renderBody = () => {
    if (!customer) {
      return <p>We're sorry but the ID provided is not valid.</p>;
    }

    if (!isVerified) {
      return (
        <Verification
          onComplete={() => {
            setVerified(true);
            if (
              customer.Plaid_Identity_Verification_Template_ID &&
              !idVerificationComplete &&
              readyIDV
            ) {
              setIDVSubmitting(true);
              openIDV();
            }
          }}
          customer={customer}
        />
      );
    }

    if (
      customer.Plaid_Identity_Verification_Template_ID &&
      !idVerificationComplete &&
      readyIDV
    ) {
      return (
        <Spin
          style={{ width: '100%', marginTop: 150 }}
          tip="One moment while we complete the identity verification process."
          size="large"
          spinning={isIDVSubmitting}
        />
      );
    }

    if (customer.Customer_Identity_Verification_Onboarding_Flag === 2) {
      if (iDVStatus) {
        const successSetting = idvSuccessCompleteSettings?.find(
          (setting) => setting.Settings_Name === 'IDV Only Completed - Success',
        );
        console.log(successSetting);
        // return (
        //   <div className="last-section">
        //     <p>More detail about Wire Transfer</p>
        //     <Button
        //       onClick={() => {
        //         openWireDetail();
        //       }}
        //     >
        //       Add my Wire Transfer Details
        //     </Button>
        //   </div>
        // );
        return <p>{successSetting?.Settings_String}</p>;
      }
      const failSetting = idvCompleteSettings?.find(
        (setting) => setting.Settings_Name === 'IDV Completed - Fail',
      );
      return <p>{failSetting?.Settings_String}</p>;
    }

    const failSetting = idvCompleteSettings?.find(
      (setting) => setting.Settings_Name === 'IDV Completed - Fail',
    );
    if (
      iDVResult?.Settings_Name === 'IDV Completed - Fail' &&
      failSetting?.Settings_IDV_Stop_On_Fail_Flag
    ) {
      return <p>{iDVResult.Settings_String}</p>;
    }

    if (iDVResult?.Settings_Name === 'IDV + Wire Transfer Completed') {
      return <p>{iDVResult.Settings_String}</p>;
    }

    if (!isPaymentPreferenceComplete && customer) {
      return (
        <PaymentPreference
          onComplete={(isDigitalComplete: boolean) => {
            setPaymentPreferenceComplete(true);
            setComplete(!isDigitalComplete);
            setIsDigital(isDigitalComplete);
          }}
          customer={customer}
        />
      );
    }

    if (isComplete) {
      return (
        <div className="last-section">
          <p>{completePageHeaderText}</p>
        </div>
      );
    }

    return (
      <Spin
        style={{ width: '100%' }}
        tip="One moment while we complete the bank connect process."
        size="large"
        spinning={isSubmitting}
      >
        {!isSubmitting &&
          customer.Customer_Identity_Verification_Onboarding_Flag !== 3 && (
            <div className="last-section">
              <p>{termsPageHeaderText}</p>
              <div className="checkbox-wrapper">
                <Checkbox onChange={(e) => setChecked(e.target.checked)}>
                  By checking this box you agree to the Tifle, LLC{' '}
                  <a href={`${basicUrl}tos`} target="_blank">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href={`${basicUrl}privacy-policy`} target="_blank">
                    Privacy Policy.
                  </a>
                </Checkbox>
              </div>
              <div className="confirm-message-box">
                After you click the Connect My Bank Account button a window will
                open and you may begin. During the process some banks may prompt
                you with a checkbox asking if you wish to share your data.{' '}
                <span className="red-underline-text">
                  Please make sure the box is checked.
                </span>{' '}
                Sharing data allows us to verify your identity and is{' '}
                <span className="red-underline-text">required</span> before a
                payment can be made to you.
              </div>
              <Button
                onClick={() => {
                  open();
                }}
                disabled={!checked}
              >
                Connect my Bank Account
              </Button>
            </div>
          )}
        {!isSubmitting &&
          customer.Customer_Identity_Verification_Onboarding_Flag === 3 && (
            <div className="last-section">
              <p>{iDVResult?.Settings_String}</p>
              <Button
                onClick={() => {
                  openWireDetail();
                }}
              >
                Add my Wire Transfer Details
              </Button>
            </div>
          )}
      </Spin>
    );
  };

  if (loadingGUIDValidate) {
    return null;
  }

  return (
    <Root>
      <header className="header">
        {customer?.Organization_Logo_URL && (
          <img src={customer?.Organization_Logo_URL} alt="brokersafe-logo" />
        )}
      </header>
      <div className="container">{renderBody()}</div>
      <footer>
        <div className="tifle-logo-wrapper">
          <div>Powered By</div>
          <img src={BrokersafeLogo} alt="brokersafe-logo" />
        </div>
      </footer>
    </Root>
  );
};

export default Plaid;
